import {forwardRef, useId, useState, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {IconButton} from '../IconButton';
import {Stack} from '../Stack';
import {Typography} from '../Typography';
import {CloseIcon, EyeIcon, EyeOffIcon} from '../icons';

export interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'className'> {
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	icon?: ReactNode;
	onClear?: () => void;
	className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
	label,
	error,
	hint,
	required = false,
	icon,
	onClear,
	id,
	type,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const [revealed, setRevealed] = useState(false);
	const inputId = id ?? generatedId;
	const messageId = `${inputId}-message`;
	const message = error ?? hint;
	const isPassword = type === 'password';
	const trailing = [onClear, isPassword].filter(Boolean).length;

	return (
		<div data-slot='input-field' className={cn(className, 'flex flex-col gap-1')}>
			{label &&
				<Stack as='label' htmlFor={inputId} gap={0.5} className='w-fit'>
					<Typography variant='small1' color='textSecondary'>{label}</Typography>
					{required &&
						<Typography variant='small1' color='critical' aria-hidden>*</Typography>}
				</Stack>}
			<div className='relative flex items-center'>
				{icon && <span aria-hidden className={iconStyles}>{icon}</span>}
				<input
					ref={ref}
					id={inputId}
					data-slot='input'
					type={isPassword && revealed ? 'text' : type}
					aria-required={required || undefined}
					aria-invalid={error ? true : undefined}
					aria-describedby={message ? messageId : undefined}
					className={cn(
						inputStyles,
						icon && 'pl-9',
						trailing === 1 && 'pr-9',
						trailing === 2 && 'pr-16',
						error && 'border-tone-red'
					)}
					{...props}/>
				{trailing > 0 &&
					<Stack gap={0} className='absolute right-1'>
						{onClear &&
							<IconButton
								size='sm'
								aria-label='Clear'
								icon={<CloseIcon/>}
								disabled={props.disabled || props.readOnly}
								onClick={onClear}/>}
						{isPassword &&
							<IconButton
								size='sm'
								aria-label={revealed ? 'Hide password' : 'Show password'}
								aria-pressed={revealed}
								icon={revealed ? <EyeOffIcon/> : <EyeIcon/>}
								disabled={props.disabled}
								onClick={() => setRevealed(on => !on)}/>}
					</Stack>}
			</div>
			{message &&
				<Typography id={messageId} variant='small2' color={error ? 'critical' : 'textMuted'}>
					{message}
				</Typography>}
		</div>
	);
});

Input.displayName = 'Input';

// h-8 is Button's md height, so the two line up in a toolbar.
const inputStyles =
	'h-8 w-full min-w-0 rounded-md border border-foreground-400 bg-transparent px-3 text-sm text-foreground-contrast ' +
	'transition-colors placeholder:text-foreground-300 ' +
	'[&::-ms-reveal]:hidden ' +
	'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-main-light ' +
	'disabled:cursor-not-allowed disabled:opacity-50';

const iconStyles = 'pointer-events-none absolute left-3 text-foreground-300 [&_svg]:size-4';
